/**
 * "Cosa hai già mandato" — Magic-Link gallery (v1.43).
 * -------------------------------------------------------
 * Renders below the picker on /upload/:token. Read-only for videos
 * older than 2 hours; recent ones can be deleted with an "oops" undo
 * button.
 *
 * Cache strategy (aggressive, per product owner):
 *   1. localStorage snapshot of the last /history payload → shown
 *      immediately on next page load, so the salon on flaky 3G sees
 *      the gallery BEFORE the network comes back.
 *   2. Fresh /history fetch runs in background and replaces the
 *      snapshot on success.
 *   3. Thumbnail responses carry `max-age=86400, immutable` server
 *      side — the browser never re-fetches a thumb it already has.
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Play, Trash2, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { api, apiError, BACKEND_URL } from "../../lib/api";

const CACHE_PREFIX = "mp:upload-history:";

function readCache(token) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + token);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Ignore stale caches (>7 days) so the client doesn't stare at
    // ghosts if they haven't opened the link in a while.
    if (Date.now() - (parsed._cachedAt || 0) > 7 * 24 * 3600 * 1000) return null;
    return parsed.data;
  } catch (_) {
    return null;
  }
}

function writeCache(token, data) {
  try {
    localStorage.setItem(
      CACHE_PREFIX + token,
      JSON.stringify({ _cachedAt: Date.now(), data }),
    );
  } catch (_) {
    /* quota exceeded / private mode — silently skip */
  }
}

function formatSessionHeader(started_at, count) {
  const d = new Date(started_at);
  const day = d.toLocaleDateString("it-IT", {
    weekday: "long", day: "numeric", month: "long",
  });
  const time = d.toLocaleTimeString("it-IT", {
    hour: "2-digit", minute: "2-digit",
  });
  const noun = count === 1 ? "video" : "video";
  return `${day} · ${time} · ${count} ${noun}`;
}

export function HistorySection({ token, refreshKey }) {
  const [data, setData] = useState(() => readCache(token));
  const [loading, setLoading] = useState(!data);
  const [expandedSessions, setExpandedSessions] = useState(() => new Set());
  const [previewItem, setPreviewItem] = useState(null); // { id, file_name, preview_url }
  const [deletingId, setDeletingId] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      const { data: fresh } = await api.get(`/upload-link/${token}/history`);
      setData(fresh);
      writeCache(token, fresh);
    } catch (e) {
      // Silent: the cached snapshot (if any) is already showing.
      void apiError(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshKey]);

  // Auto-expand the newest session so the client immediately sees the
  // clips they just uploaded (the most common reason to look).
  useEffect(() => {
    if (data?.sessions?.length && expandedSessions.size === 0) {
      const first = data.sessions[0];
      const key = first.session_id || first.items[0]?.id;
      if (key) setExpandedSessions(new Set([key]));
    }
  }, [data, expandedSessions.size]);

  const toggleSession = (key) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const doDelete = async (itemId) => {
    if (!window.confirm("Cancellare questo video? Non potrai recuperarlo.")) return;
    setDeletingId(itemId);
    try {
      await api.delete(`/upload-link/${token}/media/${itemId}`);
      // Optimistic: drop the item from the current data snapshot.
      setData((prev) => {
        if (!prev) return prev;
        const sessions = prev.sessions
          .map((s) => ({ ...s, items: s.items.filter((i) => i.id !== itemId) }))
          .filter((s) => s.items.length > 0)
          .map((s) => ({ ...s, count: s.items.length }));
        const next = { ...prev, sessions };
        writeCache(token, next);
        return next;
      });
      toast.success("Video cancellato");
    } catch (e) {
      toast.error(apiError(e) || "Cancellazione fallita");
    } finally {
      setDeletingId(null);
    }
  };

  const sessions = data?.sessions || [];
  const isEmpty = !loading && sessions.length === 0;

  if (isEmpty) return null; // nothing to show → don't clutter the page

  return (
    <div
      data-testid="upload-history-section"
      className="rounded-3xl border-2 border-[var(--mp-ink)] bg-white p-4 sm:p-5 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            Cosa hai già mandato
          </div>
          <div className="font-display text-base sm:text-lg leading-tight">
            Ultimi 30 giorni
          </div>
        </div>
        {loading && (
          <Loader2 className="h-4 w-4 animate-spin text-[var(--mp-muted)]" />
        )}
      </div>

      <div className="space-y-2">
        {sessions.map((s) => {
          const key = s.session_id || s.items[0]?.id || Math.random();
          const isOpen = expandedSessions.has(key);
          return (
            <div
              key={key}
              data-testid="upload-history-session"
              className="rounded-2xl border border-[var(--mp-border)] overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleSession(key)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-left bg-neutral-50 hover:bg-neutral-100 transition-colors"
              >
                <div className="text-sm font-medium leading-tight text-[var(--mp-ink)]">
                  {formatSessionHeader(s.started_at, s.count)}
                </div>
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-[var(--mp-muted)]" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-[var(--mp-muted)]" />
                )}
              </button>

              {isOpen && (
                <div className="p-3 grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                  {s.items.map((item) => (
                    <HistoryTile
                      key={item.id}
                      token={token}
                      item={item}
                      onOpen={() => setPreviewItem(item)}
                      onDelete={() => doDelete(item.id)}
                      deleting={deletingId === item.id}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {previewItem && (
        <PreviewModal
          token={token}
          item={previewItem}
          onClose={() => setPreviewItem(null)}
        />
      )}
    </div>
  );
}


function HistoryTile({ token, item, onOpen, onDelete, deleting }) {
  // Compose the absolute thumb URL so the browser hits our backend
  // directly (skipping the SPA router that would 404).
  const thumbSrc = `${BACKEND_URL}${item.thumbnail_url}`;
  const seq = item.clip_sequence;
  const label = (item.clip_label || "").trim();
  return (
    <div
      data-testid={`upload-history-tile-${item.id}`}
      className="relative group"
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Apri anteprima di ${item.file_name || "video"}`}
        className="w-full aspect-[3/2] rounded-lg overflow-hidden border border-[var(--mp-border)] bg-neutral-100 relative"
      >
        <img
          src={thumbSrc}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            // If Dropbox couldn't generate a poster, show a play icon
            // placeholder instead of a broken-image glyph.
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="absolute inset-0 grid place-items-center bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity">
          <Play className="h-8 w-8 text-white drop-shadow" fill="currentColor" />
        </div>
        {seq != null && (
          <div className="absolute top-1 left-1 h-5 min-w-[22px] px-1.5 rounded-full bg-[var(--mp-yellow,#facc15)] border border-[var(--mp-ink)] text-[var(--mp-ink)] text-[10px] font-bold grid place-items-center">
            {String(seq).padStart(2, "0")}
          </div>
        )}
      </button>
      {label && (
        <div
          className="mt-1 text-[10px] leading-tight font-semibold text-[var(--mp-ink)] truncate"
          title={label}
        >
          {label}
        </div>
      )}
      {item.can_delete && (
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          data-testid={`upload-history-delete-${item.id}`}
          title="Cancella (disponibile per 2 ore)"
          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 border border-red-300 text-red-600 grid place-items-center hover:bg-red-50 disabled:opacity-50 shadow-sm"
        >
          {deleting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Trash2 className="h-3 w-3" />
          )}
        </button>
      )}
    </div>
  );
}


function PreviewModal({ token, item, onClose }) {
  const src = `${BACKEND_URL}${item.preview_url}`;
  // Close on ESC — small UX polish so the modal doesn't feel trapped.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      data-testid="upload-history-preview-modal"
      className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl bg-black rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Chiudi"
          className="absolute top-2 right-2 h-9 w-9 rounded-full bg-white/95 text-black grid place-items-center shadow z-10"
          data-testid="upload-history-preview-close"
        >
          <X className="h-4 w-4" />
        </button>
        {/* Native <video> with the Dropbox temp link — the browser
            streams directly from Dropbox CDN, no bandwidth on our side. */}
        <video
          src={src}
          controls
          autoPlay
          playsInline
          className="w-full max-h-[80vh] bg-black"
          data-testid="upload-history-preview-video"
        />
        {item.file_name && (
          <div className="p-3 text-xs text-white/80 truncate">{item.file_name}</div>
        )}
      </div>
    </div>
  );
}
